import { useState, useEffect, useCallback } from 'react';
import { webhookLogger, WebhookLogEntry } from '../../utils/webhookLogger';
import { chatLogger } from '../../utils/logger';

export interface TriggerLogsSummary {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  successRate: number;
  averageResponseTime: number;
  activeTriggers: number;
  lastExecution?: string;
}

export function useTriggerLogs(chatbotId?: string) {
  const [logs, setLogs] = useState<WebhookLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!chatbotId) {
      setLogs([]);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      chatLogger.conversation('Fetching trigger logs for chatbot', { chatbotId });
      const fetchedLogs = await webhookLogger.getLogsForChatbot(chatbotId);
      setLogs(fetchedLogs);
      chatLogger.conversation('Successfully fetched trigger logs', { 
        chatbotId, 
        logCount: fetchedLogs.length 
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch logs';
      setError(errorMessage);
      chatLogger.error('Failed to fetch trigger logs', { 
        chatbotId, 
        error: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const refreshLogs = useCallback(() => {
    fetchLogs();
  }, [fetchLogs]);

  const clearLogs = useCallback(async () => {
    if (!chatbotId) return;

    try {
      await webhookLogger.clearLogs(chatbotId);
      setLogs([]);
      chatLogger.conversation('Cleared trigger logs for chatbot', { chatbotId });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear logs';
      setError(errorMessage);
      chatLogger.error('Failed to clear trigger logs', { 
        chatbotId, 
        error: errorMessage 
      });
    }
  }, [chatbotId]);

  const summary: TriggerLogsSummary = {
    totalExecutions: logs.length,
    successfulExecutions: logs.filter(log => 
      log.response_status !== undefined && log.response_status >= 200 && log.response_status < 300
    ).length,
    failedExecutions: logs.filter(log => 
      log.response_status === undefined || log.response_status < 200 || log.response_status >= 300
    ).length,
    successRate: logs.length > 0 
      ? (logs.filter(log => 
          log.response_status !== undefined && log.response_status >= 200 && log.response_status < 300
        ).length / logs.length) * 100 
      : 0,
    averageResponseTime: logs.length > 0 
      ? logs
          .filter(log => log.execution_time_ms !== undefined)
          .reduce((sum, log) => sum + (log.execution_time_ms || 0), 0) / 
        logs.filter(log => log.execution_time_ms !== undefined).length
      : 0,
    activeTriggers: [...new Set(logs.map(log => log.trigger_name).filter(Boolean))].length,
    lastExecution: logs.length > 0 ? logs[0].timestamp : undefined
  };

  return {
    logs,
    summary,
    loading,
    error,
    refreshLogs,
    clearLogs
  };
}
